import React from 'react';
import { Alert, Button, Card, Empty, Space, Spin, Table, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/common/MainLayout';
import { useInspectUserSavesQuery, useInspectUserSaveQuery, InspectedSave } from '../../api/adminSaveViewApi';

const AdminUserSavesPage: React.FC = () => {
  const { userId = '', saveId = '' } = useParams();
  const { t } = useTranslation('admin');
  const role = useSelector((state: any) => state.auth.user?.role);
  const allowed = String(role).toLowerCase() === 'superuser';
  const list = useInspectUserSavesQuery(userId, { skip: !allowed, refetchOnMountOrArgChange: true });
  const view = useInspectUserSaveQuery({ userId, saveId }, { skip: !allowed || !saveId, refetchOnMountOrArgChange: true });
  const date = (v: string | null | undefined) => v ? v.slice(0, 10) : '—';
  return <MainLayout><Space direction="vertical" style={{ width: '100%' }} size="middle">
    <Link to="/admin/users">← {t('inspection.back')}</Link>
    <Typography.Title level={3}>{t('inspection.title')}</Typography.Title>
    <Typography.Text type="secondary">{userId}</Typography.Text>
    {!allowed ? <Alert type="error" message={t('inspection.forbidden')} /> : <>
      <Alert type="info" showIcon message={t('inspection.readOnly')} description={t('inspection.serverOnly')} />
      {list.isError && <Alert type="error" message={t('inspection.error')} action={<Button onClick={() => list.refetch()}>{t('inspection.retry')}</Button>} />}
      <Table<InspectedSave> rowKey="id" loading={list.isFetching} dataSource={list.data || []}
        locale={{ emptyText: t('inspection.noSaves') }} pagination={{ pageSize: 10 }} columns={[
          { title: t('inspection.save'), render: (_, s) => <Link to={`/admin/users/${userId}/saves/${s.id}`}>{s.name || s.id}</Link> },
          { title: t('inspection.day'), render: (_, s) => date(s.cursor?.sim_date) },
          { title: t('inspection.status'), dataIndex: 'status' },
          { title: t('inspection.updated'), render: (_, s) => date(s.updatedAt) },
        ]} />
      {saveId && view.isFetching && <Spin />}
      {saveId && view.isError && <Alert type="error" message={t('inspection.error')} action={<Button onClick={() => view.refetch()}>{t('inspection.retry')}</Button>} />}
      {saveId && !view.isFetching && view.currentData && <>
        <Typography.Title level={4}>{view.currentData.save.name || saveId}</Typography.Title>
        {!view.currentData.plots.length && <Empty description={t('inspection.noPlots')} />}
        {view.currentData.plots.map((plot) => <Card key={plot.id} title={plot.name || plot.id}>
          <img alt={t('inspection.plan')} src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(plot.svg)}`} style={{ width: '100%', maxHeight: 850, objectFit: 'contain' }} />
          <Table rowKey="id" size="small" pagination={false} dataSource={plot.beds} columns={[
            { title: t('inspection.bed'), render: (_, bed) => bed.name || bed.id },
            { title: t('inspection.plantings'), render: (_, bed) => bed.plantings.length ? bed.plantings.map((p, i) =>
              <div key={`${p.id}-${i}`}>{p.crop} × {p.quantity} · {date(p.fromDate)} — {date(p.toDate)} · {p.status}</div>) : '—' },
          ]} />
          {!!plot.structures.length && <Table rowKey="id" size="small" pagination={false} dataSource={plot.structures} columns={[
            { title: t('inspection.structure'), render: (_, s) => s.name || s.subtype },
            { title: t('inspection.dates'), render: (_, s) => `${date(s.activeFrom)} — ${date(s.activeTo)}` },
          ]} />}
        </Card>)}
      </>}
    </>}
  </Space></MainLayout>;
};
export default AdminUserSavesPage;
